// src/utils/authFetch.js

export function getAuthToken() {
  return localStorage.getItem('authToken') || null;
}

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem('authToken', token);
    localStorage.setItem('authTokenSetAt', Date.now().toString());
  } else {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenSetAt');
  }
}

export function clearAuthToken() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authTokenSetAt');
}

/**
 * Google ID tokens expire after 1 hour.
 * LinkedIn backend JWTs also expire after 1 hour (JWT_EXPIRY_SECONDS = 3600).
 * We check after 55 minutes to give a small safety buffer.
 */
export function isTokenExpired() {
  const setAt = localStorage.getItem('authTokenSetAt');
  if (!setAt) return true;
  const ageMs = Date.now() - parseInt(setAt, 10);
  return ageMs > 55 * 60 * 1000; // 55 minutes
}

/**
 * Clears all auth state and redirects to /signup.
 * Called on token expiry or a 401 response from the backend.
 */
function forceLogout(reason) {
  console.warn('authFetch:', reason, '— redirecting to /signup');
  clearAuthToken();
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userFirstName');
  localStorage.removeItem('userLastName');
  localStorage.removeItem('loginSource');
  window.location.href = '/signup';
}

/**
 * Drop-in replacement for fetch() that:
 * 1. Injects Authorization: Bearer <token> on all requests
 * 2. Checks token expiry before sending — redirects to /signup if expired
 * 3. Intercepts 401 responses — redirects to /signup if backend rejects token
 *
 * Usage:
 *   import { authFetch } from '../utils/authFetch';
 *   const res = await authFetch(url, { method: 'POST', body: JSON.stringify(data) });
 */
export async function authFetch(url, options = {}) {
  const token = getAuthToken();

  if (!token || isTokenExpired()) {
    forceLogout('Token missing or expired');
    // Throw so callers don't try to use the (never-returned) response
    throw new Error('Session expired. Please log in again.');
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    forceLogout('Backend returned 401 Unauthorized');
    throw new Error('Unauthorized. Please log in again.');
  }

  return response;
}

export default authFetch;